/**
 * useDashboardWidgetPanel — shared open-state coordination for the
 * admin dashboard header widgets (Collection Email Audit, Review Funnel,
 * New Reviews Alert).
 *
 * Problem this solves (07 Feb 2026):
 *   Each widget owned its own `open` React state. On mobile all three
 *   panels are ~380px wide and `absolute right-0 z-50`, so opening a
 *   second panel while the first was still open produced visible
 *   overlap on a 412px viewport — text/buttons bleeding through each
 *   other on production saturnstyling.ie.
 *
 * Design:
 *   - Only ONE panel can be open at a time.
 *   - Opening a panel dispatches a `dashboard-widget-opened` custom
 *     event carrying the panel id; sibling panels listen and close
 *     themselves when they see an id !== their own.
 *   - Click outside the panel (backdrop tap) closes the current panel.
 *   - Escape key closes the current panel.
 *
 * Why a custom event rather than Context/prop-drilling:
 *   - Zero changes to DashboardPage.jsx layout
 *   - Zero new dependencies between widget files
 *   - Works if a future widget is added without any registration
 */
import { useCallback, useEffect, useRef, useState } from "react";

const EVENT_NAME = "dashboard-widget-opened";

export function useDashboardWidgetPanel(id) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const tileRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback(() => {
    setOpen((current) => {
      const next = !current;
      if (next) {
        // Broadcast so siblings can dismiss themselves.
        window.dispatchEvent(
          new CustomEvent(EVENT_NAME, { detail: { id } })
        );
      }
      return next;
    });
  }, [id]);

  // Sibling coordination
  useEffect(() => {
    const handler = (ev) => {
      if (ev?.detail?.id && ev.detail.id !== id) {
        setOpen(false);
      }
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, [id]);

  // Click-outside + Escape (only while open — cheap listeners, but no
  // reason to keep them attached when the panel is closed)
  useEffect(() => {
    if (!open) return undefined;

    const onDocPointer = (ev) => {
      const t = ev.target;
      if (!(t instanceof Node)) return;
      if (panelRef.current && panelRef.current.contains(t)) return;
      if (tileRef.current && tileRef.current.contains(t)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") setOpen(false);
    };

    // `pointerdown` fires before `click`, catching taps on other widgets
    // BEFORE they run their own toggle logic — which prevents the "close
    // then re-open" flicker when tapping a different tile.
    document.addEventListener("pointerdown", onDocPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return { open, toggle, close, panelRef, tileRef };
}
